import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Grid, X } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from './AuthContext';

// Outbound calls from the browser (Twilio Voice SDK). Lives above the routes
// so a call keeps going while the rep moves between pages. The server decides
// whether each call is allowed (routes/browserPhone.js) — this only asks.

type CallPhase = 'checking' | 'connecting' | 'ringing' | 'connected' | 'ended' | 'error';

interface ActiveCall {
  leadId: string;
  leadName: string;
  to: string;
  phase: CallPhase;
  message?: string;
  connectedAt?: number;
  muted: boolean;
}

interface BrowserPhoneValue {
  enabled: boolean;
  callerId: string | null;
  busy: boolean;
  placeCall: (lead: { id: string; name: string; phone: string }) => Promise<void>;
}

const Ctx = createContext<BrowserPhoneValue>({ enabled: false, callerId: null, busy: false, placeCall: async () => {} });
export const useBrowserPhone = () => useContext(Ctx);

export const formatPhone = (e164: string | null | undefined) => {
  const d = String(e164 || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(e164 || '');
};

const errorText = (err: any): string => {
  const code = err?.code;
  if (code === 31401 || code === 31208 || /permission|notallowed/i.test(String(err?.name || err?.message))) {
    return 'Microphone access is blocked. Allow the microphone for this site in your browser, then try again.';
  }
  if (code === 31005 || code === 31009 || code === 53000) return 'Lost the connection to the phone service. Check your internet and try again.';
  return err?.response?.data?.error || err?.message || 'The call could not be placed.';
};

export const BrowserPhoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [callerId, setCallerId] = useState<string | null>(null);
  const [active, setActive] = useState<ActiveCall | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);
  const [now, setNow] = useState(Date.now());
  const deviceRef = useRef<any>(null);
  const tokenExpiresRef = useRef(0);
  const callRef = useRef<any>(null);

  useEffect(() => {
    if (!user) { setEnabled(false); setCallerId(null); return; }
    api.get('/browser-phone/status')
      .then(r => { setEnabled(!!r.data.enabled); setCallerId(r.data.callerId || null); })
      .catch(() => setEnabled(false));
  }, [user]);

  useEffect(() => {
    if (active?.phase !== 'connected') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active?.phase]);

  // Leaving the page mid-call would silently drop it.
  useEffect(() => {
    const live = active && ['connecting', 'ringing', 'connected'].includes(active.phase);
    if (!live) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);

  const getDevice = useCallback(async () => {
    if (deviceRef.current && Date.now() < tokenExpiresRef.current - 60_000) return deviceRef.current;
    const r = await api.post('/browser-phone/token');
    const { Device } = await import('@twilio/voice-sdk');
    tokenExpiresRef.current = Date.now() + (r.data.ttlSeconds || 3600) * 1000;
    if (deviceRef.current) {
      deviceRef.current.updateToken(r.data.token);
    } else {
      // Outbound only: never register() (no incoming calls to the browser).
      deviceRef.current = new Device(r.data.token, { closeProtection: true, codecPreferences: ['opus', 'pcmu'] as any });
    }
    return deviceRef.current;
  }, []);

  const endWith = (leadId: string, phase: CallPhase, message?: string) => {
    callRef.current = null;
    setShowKeypad(false);
    setActive(a => (a ? { ...a, phase, message } : a));
    window.dispatchEvent(new CustomEvent('browser-phone:ended', { detail: { leadId } }));
  };

  const placeCall = useCallback(async (lead: { id: string; name: string; phone: string }) => {
    if (callRef.current) return;
    setShowKeypad(false);
    setActive({ leadId: lead.id, leadName: lead.name, to: lead.phone, phase: 'checking', muted: false });
    try {
      const check = await api.post('/browser-phone/check', { leadId: lead.id, to: lead.phone });
      if (!check.data.ok) {
        setActive(a => (a ? { ...a, phase: 'error', message: check.data.reason } : a));
        return;
      }
      setActive(a => (a ? { ...a, to: check.data.to, phase: 'connecting' } : a));
      const device = await getDevice();
      const call = await device.connect({ params: { To: check.data.to, leadId: lead.id } });
      callRef.current = call;
      // The server dials with answerOnBridge, so Twilio holds 'accept' until
      // the lead actually picks up; 'ringing' fires while their phone rings.
      call.on('ringing', () => setActive(a => (a && !a.connectedAt ? { ...a, phase: 'ringing' } : a)));
      call.on('accept', () => setActive(a => (a && !a.connectedAt ? { ...a, phase: 'connected', connectedAt: Date.now() } : a)));
      call.on('disconnect', () => endWith(lead.id, 'ended'));
      call.on('cancel', () => endWith(lead.id, 'ended'));
      call.on('reject', () => endWith(lead.id, 'ended', 'Call rejected.'));
      call.on('error', (err: any) => endWith(lead.id, 'error', errorText(err)));
    } catch (err: any) {
      callRef.current = null;
      setActive(a => (a ? { ...a, phase: 'error', message: errorText(err) } : a));
    }
  }, [getDevice]);

  const hangUp = () => { callRef.current?.disconnect(); };
  const toggleMute = () => {
    const c = callRef.current;
    if (!c) return;
    const next = !c.isMuted();
    c.mute(next);
    setActive(a => (a ? { ...a, muted: next } : a));
  };
  const sendDigit = (d: string) => callRef.current?.sendDigits(d);

  const live = !!active && ['checking', 'connecting', 'ringing', 'connected'].includes(active.phase);
  const elapsed = active?.connectedAt ? Math.max(0, Math.floor((now - active.connectedAt) / 1000)) : 0;
  const timer = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
  const phaseText: Record<CallPhase, string> = {
    checking: 'Checking…', connecting: 'Connecting…', ringing: 'Ringing…', connected: timer, ended: 'Call ended', error: 'Call not placed',
  };

  return (
    <Ctx.Provider value={{ enabled, callerId, busy: live, placeCall }}>
      {children}
      {active && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-80 z-50 bg-white border border-gray-200 rounded-xl shadow-2xl p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{active.leadName}</div>
              <div className="text-xs text-gray-500">{formatPhone(active.to)}</div>
            </div>
            {!live && (
              <button onClick={() => setActive(null)} className="p-1 text-gray-400 hover:text-gray-600" title="Close">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className={`mt-2 text-sm font-medium ${active.phase === 'error' ? 'text-red-700' : active.phase === 'connected' ? 'text-green-700' : 'text-gray-700'}`}>
            {phaseText[active.phase]}
          </div>
          {active.message && <div className="mt-1 text-xs text-gray-600">{active.message}</div>}
          {callerId && live && <div className="mt-1 text-xs text-gray-400">Showing caller ID {formatPhone(callerId)}</div>}

          {showKeypad && live && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map(d => (
                <button key={d} onClick={() => sendDigit(d)} className="py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium">{d}</button>
              ))}
            </div>
          )}

          {live && (
            <div className="mt-3 flex items-center gap-2">
              <button onClick={toggleMute} disabled={!callRef.current} className={`flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm border ${active.muted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}>
                {active.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}{active.muted ? 'Unmute' : 'Mute'}
              </button>
              <button onClick={() => setShowKeypad(k => !k)} disabled={!callRef.current} className="px-3 py-2 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50" title="Keypad">
                <Grid className="h-4 w-4" />
              </button>
              <button onClick={hangUp} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm bg-red-600 text-white hover:bg-red-700">
                <PhoneOff className="h-4 w-4" />Hang up
              </button>
            </div>
          )}
          {!live && active.phase === 'ended' && (
            <div className="mt-2 text-xs text-gray-500 inline-flex items-center gap-1"><Phone className="h-3 w-3" />Logged on the lead's timeline.</div>
          )}
        </div>
      )}
    </Ctx.Provider>
  );
};
